import { Fragment, useMemo } from "react";

import { cn } from "@/utils/css";

const PLOT_TOP = 23; // room above the top gridline for its label
const ROW_H = 50.67; // px between adjacent gridlines
const Y_LABEL_W = 40;
const PLOT_LEFT = 49; // bars start right of the y-axis labels
const X_AXIS_H = 44; // gap + period label + change label

const formatCurrency = (value) => `$${Math.round(value).toLocaleString("en-US")}`;

const formatChange = (change, index) => (index === 0 ? "0.0%" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`);

const changeColor = (change) =>
    change > 0 ? "text-cm-p-on-container-green" : change < 0 ? "text-cm-p-on-container-red" : "text-cm-p-text-secondary";

const BAR_CLASSES = {
    market: "bg-cm-p-graph-market-average-30",
    topPerforming: "bg-cm-p-graph-high-perf-30",
};
const SKELETON_BAR_CLASSES = {
    market: "bg-cm-p-surface-hover",
    topPerforming: "bg-cm-p-surface-container",
};

// Grouped bars (market average, high performers) with the company's value drawn as a line over them.
// `metric` is a MetricData-shaped object (companyBars / marketBars / topPerformingBars); `yTicks` are the
// gridline values, ascending — bars are drawn from the lowest tick, as in the design. `skeleton` renders a
// ghost version for metrics that are not configured yet.
export const BenchmarkChart = ({ metric, yTicks, skeleton = false, className }) => {
    const plotHeight = (yTicks.length - 1) * ROW_H;
    const yMin = yTicks[0];
    const yMax = yTicks[yTicks.length - 1];
    const toPx = (value) => Math.min(1, Math.max(0, (value - yMin) / (yMax - yMin))) * plotHeight;

    const periods = useMemo(
        () =>
            metric.companyBars.map((bar, i) => ({
                label: bar.label,
                change: bar.percentageChange,
                company: bar.value,
                market: metric.marketBars[i]?.value ?? yMin,
                topPerforming: metric.topPerformingBars[i]?.value ?? yMin,
            })),
        [metric, yMin],
    );
    const count = periods.length;

    const linePoints = periods.map((p, i) => `${i + 0.5},${(plotHeight - toPx(p.company)).toFixed(2)}`).join(" ");
    const barClasses = skeleton ? SKELETON_BAR_CLASSES : BAR_CLASSES;
    const plotStyle = { top: PLOT_TOP, left: PLOT_LEFT, height: plotHeight };

    return (
        <div className={cn("relative w-full", className)} style={{ height: PLOT_TOP + plotHeight + X_AXIS_H }} aria-hidden={skeleton}>
            {yTicks.map((tick, i) => {
                const y = PLOT_TOP + plotHeight - toPx(tick);
                return (
                    <Fragment key={tick}>
                        <div
                            className={cn("absolute inset-x-0 border-t", i === 0 ? "border-cm-p-outline" : "border-cm-p-outline-variation")}
                            style={{ top: y }}
                        />
                        {skeleton ? (
                            <div
                                className="absolute left-0 h-[17px] w-[42px] rounded-[4px] bg-cm-p-surface-container"
                                style={{ top: y - 21 }}
                            />
                        ) : (
                            <span
                                className="absolute left-0 text-right text-xs/[19px] tracking-[0.24px] text-cm-p-text-primary"
                                style={{ top: y - 22, width: Y_LABEL_W }}
                            >
                                {formatCurrency(tick)}
                            </span>
                        )}
                    </Fragment>
                );
            })}

            <div
                className="absolute right-0 grid items-end"
                style={{ ...plotStyle, gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
            >
                {periods.map((p) => (
                    <div key={p.label} className="flex items-end justify-center gap-0.5">
                        <div className={cn("w-4 rounded-t-[2px]", barClasses.market)} style={{ height: toPx(p.market) }} />
                        <div className={cn("w-4 rounded-t-[2px]", barClasses.topPerforming)} style={{ height: toPx(p.topPerforming) }} />
                    </div>
                ))}
            </div>

            <svg
                className="absolute right-0"
                style={{ ...plotStyle, width: `calc(100% - ${PLOT_LEFT}px)` }}
                viewBox={`0 0 ${count} ${plotHeight}`}
                preserveAspectRatio="none"
            >
                <polyline
                    points={linePoints}
                    fill="none"
                    className={skeleton ? "stroke-cm-p-outline" : "stroke-cm-p-accent-500"}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
            {!skeleton ? (
                <div className="absolute right-0" style={plotStyle}>
                    {periods.map((p, i) => (
                        <div
                            key={p.label}
                            className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cm-p-accent-500"
                            style={{ left: `${((i + 0.5) / count) * 100}%`, top: plotHeight - toPx(p.company) }}
                        />
                    ))}
                </div>
            ) : null}

            <div
                className="absolute right-0 grid"
                style={{ top: PLOT_TOP + plotHeight + 12, left: PLOT_LEFT, gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
            >
                {periods.map((p, i) =>
                    skeleton ? (
                        <div key={p.label} className="flex justify-center">
                            <div className="h-[17px] w-[42px] rounded-[4px] bg-cm-p-surface-container" />
                        </div>
                    ) : (
                        <div key={p.label} className="flex flex-col items-center text-center">
                            <span
                                className={cn(
                                    "text-xs/[19px] text-cm-p-text-primary",
                                    p.label === "current" ? "italic" : "tracking-[0.24px]",
                                )}
                            >
                                {p.label}
                            </span>
                            <span className={cn("text-[10px]/[15px] tracking-[0.2px]", changeColor(i === 0 ? 0 : p.change))}>
                                {formatChange(p.change, i)}
                            </span>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
